import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import styled, { keyframes, css } from 'styled-components';
import { UserPet } from '@typings/pets/pet.interface';
import {
  PetPersonalityType,
  PetPersonalityTypeScripts,
} from '../PetPersonality.ts/PetPersonalityType';
import { petActivePetIDState } from '@atom/setting/petFloating';
import { useRecoilState } from 'recoil';
import { useDocument } from '@nandorojo/swr-firestore';
import { CollectionPath } from '@typings/collections.enum';
import { currentUserIDState } from '@atom/setting/user';
import { usePetLabel } from '../../hooks/usePetLabel';

// 펫 성격 설정 문서 타입
type PersonalitiesDoc = {
  types: Record<string, string>;
  scripts: Record<string, string[]>;
  updatedAt?: any;
};

interface Props {
  onClose?: () => void;
}

const FloatingPet: React.FC<Props> = ({ onClose }) => {
  const [x, setX] = useState<number>(window.innerWidth * 0.2);
  const [targetX, setTargetX] = useState<number>(window.innerWidth * 0.2);
  const [isJumping, setIsJumping] = useState(false);
  const [speech, setSpeech] = useState<string>('');
  const rafRef = useRef<number | null>(null);
  const dialogueTimerRef = useRef<NodeJS.Timeout>();
  const dialogueHideTimerRef = useRef<NodeJS.Timeout>();
  const { petLabel } = usePetLabel();
  const [activePetID] = useRecoilState(petActivePetIDState);
  const [currentUserId] = useRecoilState(currentUserIDState);
  const { data: pet } = useDocument<UserPet>(
    currentUserId
      ? `${CollectionPath.USERS}/${currentUserId}/pets/${activePetID}`
      : '',
    { listen: true },
  );

  // 커스텀 성격/대사 설정 가져오기
  const { data: personalitiesData } = useDocument<PersonalitiesDoc>(
    'petSetting/personalities',
    { listen: true },
  );

  // 기본값 + 커스텀 대사 병합
  const mergedScripts = useMemo(() => {
    const defaultScripts = { ...PetPersonalityTypeScripts } as Record<
      string,
      string[]
    >;
    const customScripts = personalitiesData?.scripts || {};
    // 커스텀이 있으면 커스텀 우선, 없으면 기본값
    const merged: Record<string, string[]> = { ...defaultScripts };
    Object.keys(customScripts).forEach((key) => {
      if (customScripts[key] && customScripts[key].length > 0) {
        merged[key] = customScripts[key];
      }
    });
    return merged;
  }, [personalitiesData]);

  // 마우스 따라가기 (약하게)
  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      // 화면 가장자리로 너무 붙지 않도록 마진 적용
      const margin = 40;
      const safeX = Math.max(
        margin,
        Math.min(window.innerWidth - margin, e.clientX),
      );
      setTargetX(safeX);
    };
    window.addEventListener('mousemove', handleMove);
    return () => window.removeEventListener('mousemove', handleMove);
  }, []);

  // 부드러운 이동 (lerp)
  useEffect(() => {
    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
    const tick = () => {
      setX((prev) => lerp(prev, targetX, 0.03)); // 더 느리게 따라오도록 조정
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [targetX]);

  // 성격 기반 랜덤 대사
  const generateRandomDialogue = useCallback(() => {
    if (!pet) return;

    if (!pet.personalities || pet.personalities.length === 0) return '';
    const randomPersonality = pet.personalities[
      Math.floor(Math.random() * pet.personalities.length)
    ] as string;
    const dialogues = mergedScripts[randomPersonality];
    if (!dialogues || dialogues.length === 0) return '';
    const randomDialogue =
      dialogues[Math.floor(Math.random() * dialogues.length)];
    return randomDialogue;
  }, [pet?.personalities, mergedScripts]);

  // 대사 타이머 시작
  const startDialogueTimer = useCallback(() => {
    const setNextDialogue = () => {
      const newDialogue = generateRandomDialogue();
      setSpeech(newDialogue || '');
      // 5~8초 후 숨김
      const hideDelay = Math.floor(Math.random() * 3000) + 5000;
      dialogueHideTimerRef.current = setTimeout(() => {
        setSpeech('');
      }, hideDelay);

      // 60~120초 후 다음 출력
      const randomInterval = Math.floor(Math.random() * 60000) + 60000;
      dialogueTimerRef.current = setTimeout(() => {
        setNextDialogue();
      }, randomInterval);
    };

    // 첫 대사는 5초 후
    dialogueTimerRef.current = setTimeout(() => {
      setNextDialogue();
    }, 5000);
  }, [generateRandomDialogue]);

  useEffect(() => {
    startDialogueTimer();
    return () => {
      if (dialogueTimerRef.current) clearTimeout(dialogueTimerRef.current);
      if (dialogueHideTimerRef.current)
        clearTimeout(dialogueHideTimerRef.current);
    };
  }, [startDialogueTimer]);

  // 클릭 시 즉시 랜덤 대사 + 점프
  const handleClick = useCallback(() => {
    if (!isJumping) setIsJumping(true);
    // 기존 숨김 타이머 클리어
    if (dialogueHideTimerRef.current)
      clearTimeout(dialogueHideTimerRef.current);
    const newDialogue = generateRandomDialogue();
    setSpeech(newDialogue || '');
    // 5~8초 후 숨김
    const hideDelay = Math.floor(Math.random() * 3000) + 5000;
    dialogueHideTimerRef.current = setTimeout(() => {
      setSpeech('');
    }, hideDelay);
    // 점프 애니메이션은 0.9초만 유지
    setTimeout(() => setIsJumping(false), 900);
  }, [generateRandomDialogue, isJumping]);

  const imgURL = pet?.customImgURL;
  const name = pet?.customName || petLabel;

  return (
    <Root style={{ left: x - 32 }}>
      <PetWrap onClick={handleClick} $jump={isJumping}>
        {imgURL ? (
          <PetImg src={imgURL} alt={name} />
        ) : (
          <PetFallback>{name.slice(0, 2)}</PetFallback>
        )}
      </PetWrap>
      {speech && <Speech>{speech}</Speech>}
    </Root>
  );
};

// 더 쫀득한 점프 (스쿼시 & 스트레치 + 리바운드)
const jump = keyframes`
  0%   { transform: translateY(0) scaleX(1)   scaleY(1);   }
  10%  { transform: translateY(0) scaleX(1.06) scaleY(0.92); } /* 바닥에서 찌그러짐 */
  35%  { transform: translateY(-30px) scaleX(0.96) scaleY(1.08); } /* 위로 늘어남 */
  55%  { transform: translateY(0) scaleX(1.04) scaleY(0.94); } /* 착지 찌그러짐 */
  75%  { transform: translateY(-6px) scaleX(0.99) scaleY(1.02); } /* 리바운드 */
  100% { transform: translateY(0) scaleX(1)   scaleY(1);   }
`;

const Root = styled.div`
  position: fixed;
  bottom: 0;
  height: 150px;
  z-index: 9999;
  pointer-events: none;
`;

const PetWrap = styled.div<{ $jump: boolean }>`
  max-height: 150px;
  pointer-events: auto;
  user-select: none;
  cursor: pointer;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.25));
  transform-origin: center bottom;
  will-change: transform;
  ${({ $jump }) =>
    $jump &&
    css`
      animation: ${jump} 0.9s cubic-bezier(0.2, 0.7, 0.2, 1);
    `}
`;

const PetImg = styled.img`
  object-fit: contain;
  user-select: none;
  pointer-events: none;
`;

const PetFallback = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--cardBackgroundColor);
  border: 1px solid var(--cardLineColor);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: var(--textColor);
`;

const Speech = styled.div`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  padding: 6px 10px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.75);
  color: #fff;
  font-size: 12px;
  white-space: nowrap;
`;

// CloseBtn 스타일은 더 이상 사용하지 않으므로 제거

export default React.memo(FloatingPet);
